import {createListenerMiddleware, ListenerEffect, ThunkDispatch, UnknownAction} from "@reduxjs/toolkit";

import {userComUserRegister} from "@pg-mono/user-com";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {IRootState} from "../../store/store";
import {userSignInHit} from "../../tracking/algolytics_hits/user_sign_in_hit";
import {userSignUpHit} from "../../tracking/algolytics_hits/user_sign_up_hit";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {fetchUserUnsubscribeLink} from "../../user/api/fetch_user_unsubscribe_link";
import {getUserProfileApi} from "../../user/api/get_user_profile";
import {IUserProfile} from "../../user/types/IUserProfile";
import {authSucceeded, IAuthSucceededPayload, IPostAuthFlowContext} from "../actions/auth_succeeded_event";
import {closeFlow, nextStep, RegistrationFlowStep, startRegistrationFlow} from "../state/registration_flow_slice";
import {AuthActionType} from "../types/AuthActionType";

/**
 * Listener for handling post-authentication actions (login or registration)
 * This should be triggered after user logs in or registers via login/register form
 * without use of kontomieszkaniowe.pl
 *
 * Use cases:
 * - Two-step application forms [CU-2t405gt]
 * - Hidden prices [CU-862j5xdx7]
 * - Registration after application sent
 */
export const afterAuthListener = createListenerMiddleware<IRootState>();

const USER_PROFILE_WAIT_TIMEOUT_MS = 20000;
type AfterAuthDispatch = ThunkDispatch<IRootState, unknown, UnknownAction>;

type RegistrationFlowSnapshot = Pick<
    IRootState["registrationFlow"],
    "isActive" | "currentStep" | "propertyId" | "offerId" | "vendorId" | "userSource" | "useLeadAfterRegistration"
>;

const getRegistrationFlowSnapshot = (registrationFlow: IRootState["registrationFlow"]): RegistrationFlowSnapshot => ({
    isActive: registrationFlow.isActive,
    currentStep: registrationFlow.currentStep,
    propertyId: registrationFlow.propertyId,
    offerId: registrationFlow.offerId,
    vendorId: registrationFlow.vendorId,
    userSource: registrationFlow.userSource,
    useLeadAfterRegistration: registrationFlow.useLeadAfterRegistration
});

const getExpectedActiveFlowSnapshot = (fallbackSnapshot: RegistrationFlowSnapshot, postAuthFlowContext?: IPostAuthFlowContext): RegistrationFlowSnapshot => {
    if (!postAuthFlowContext?.flowWasActive) {
        return fallbackSnapshot;
    }

    return {
        isActive: true,
        currentStep: postAuthFlowContext.currentStep ?? fallbackSnapshot.currentStep,
        propertyId: postAuthFlowContext.registrationFlow.propertyId,
        offerId: postAuthFlowContext.registrationFlow.offerId,
        vendorId: postAuthFlowContext.registrationFlow.vendorId,
        userSource: postAuthFlowContext.registrationFlow.userSource,
        useLeadAfterRegistration: postAuthFlowContext.registrationFlow.useLeadAfterRegistration
    };
};

const isSameActiveFlow = (registrationFlow: IRootState["registrationFlow"], snapshot: RegistrationFlowSnapshot) => {
    return (
        registrationFlow.isActive &&
        snapshot.isActive &&
        registrationFlow.currentStep === snapshot.currentStep &&
        registrationFlow.propertyId === snapshot.propertyId &&
        registrationFlow.offerId === snapshot.offerId &&
        registrationFlow.vendorId === snapshot.vendorId &&
        registrationFlow.userSource === snapshot.userSource &&
        registrationFlow.useLeadAfterRegistration === snapshot.useLeadAfterRegistration
    );
};

/**
 * Waits for the profile refresh triggered by `fetchUserDataAfterAuthListener`. Every problem is reported and
 * resolved as `null` instead of aborting the caller - authentication already succeeded, so the post-auth flow
 * has to be resolved either way
 */
const takeUserProfile = async (
    listenerApi: Parameters<ListenerEffect<ReturnType<typeof authSucceeded>, IRootState, AfterAuthDispatch>>[1],
    authPayload: IAuthSucceededPayload
): Promise<IUserProfile | null> => {
    let getUserProfileResult: Awaited<ReturnType<typeof listenerApi.take>>;

    try {
        getUserProfileResult = await listenerApi.take(
            (profileAction) =>
                /**
                 * Other consumers (mounting components, concurrent listeners) emit `getUserProfile` actions that
                 * carry no usable profile and must not end the wait: a deduplicated request is rejected with
                 * `meta.condition`, and the anonymous guard resolves with a null payload while the session cache
                 * is still being revalidated. Skip both and keep waiting for the actual refresh
                 */
                (getUserProfileApi.endpoints.getUserProfile.matchFulfilled(profileAction) && profileAction.payload != null) ||
                (getUserProfileApi.endpoints.getUserProfile.matchRejected(profileAction) && !profileAction.meta.condition),
            USER_PROFILE_WAIT_TIMEOUT_MS
        );
    } catch (error) {
        if (listenerApi.signal?.aborted) {
            return null;
        }

        notifyBugsnag(error, "Failed while waiting for user profile after authentication", {authPayload});
        return null;
    }

    if (!getUserProfileResult) {
        notifyBugsnag(new Error("Timed out while waiting for user profile"), "User profile timeout after authentication", {authPayload});
        return null;
    }

    const profileAction = getUserProfileResult[0];

    if (getUserProfileApi.endpoints.getUserProfile.matchRejected(profileAction)) {
        notifyBugsnag(profileAction.error, "Failed to fetch user profile after authentication", {authPayload});
        return null;
    }

    if (!getUserProfileApi.endpoints.getUserProfile.matchFulfilled(profileAction) || !profileAction.payload) {
        notifyBugsnag(new Error("getUserProfile does not return a valid data"), "Problem with getUserProfile", {authPayload});
        return null;
    }

    return profileAction.payload;
};

export const handleAfterAuth: ListenerEffect<ReturnType<typeof authSucceeded>, IRootState, AfterAuthDispatch> = async (action, listenerApi) => {
    const authPayload = action.payload;
    const flowSnapshotAtAuth = getRegistrationFlowSnapshot(listenerApi.getState().registrationFlow);
    const flowWasActiveAtAuth = Boolean(authPayload.postAuthFlowContext?.flowWasActive || flowSnapshotAtAuth.isActive);
    const expectedActiveFlowSnapshot = getExpectedActiveFlowSnapshot(flowSnapshotAtAuth, authPayload.postAuthFlowContext);

    listenerApi.cancelActiveListeners?.();

    const userProfile = await takeUserProfile(listenerApi, authPayload);

    if (listenerApi.signal?.aborted) {
        return;
    }

    const state = listenerApi.getState();
    const hasCompleteProfile = Boolean(userProfile?.name && userProfile.phone);
    const hasPhoneVerified = Boolean(userProfile?.phone_verified);
    const afterRegister = authPayload.type === AuthActionType.REGISTRATION;

    try {
        if (flowWasActiveAtAuth) {
            if (
                expectedActiveFlowSnapshot.currentStep === RegistrationFlowStep.REGISTRATION &&
                isSameActiveFlow(state.registrationFlow, expectedActiveFlowSnapshot)
            ) {
                if (!userProfile && !afterRegister) {
                    /**
                     * Login succeeded but the profile is unknown, so there is no way to tell which onboarding
                     * step is due. Closing beats guessing - and beats leaving the user on a filled in login
                     * form that never reacts
                     */
                    listenerApi.dispatch(closeFlow());
                } else {
                    listenerApi.dispatch(nextStep({hasPhoneVerified, hasCompleteProfile}));
                }
            }
        } else if (!state.registrationFlow.isActive) {
            const oneTapContext = authPayload.authOrigin === "google_one_tap" ? authPayload.postAuthFlowContext : undefined;

            if (oneTapContext && !oneTapContext.flowWasActive) {
                listenerApi.dispatch(
                    startRegistrationFlow({
                        ...oneTapContext.registrationFlow,
                        isUserLoggedIn: true,
                        hasCompleteProfile,
                        hasPhoneVerified,
                        registrationMethod: authPayload.registrationMethod,
                        socialMediaType: authPayload.socialMediaType ?? "google",
                        userSource: oneTapContext.registrationFlow.userSource ?? authPayload.userSource,
                        gtmSourceSection: oneTapContext.registrationFlow.gtmSourceSection ?? authPayload.gtmSourceSection
                    })
                );
            } else if (afterRegister) {
                const validRegistrationFlowSources: UserSource[] = [UserSource.NAVIGATION, UserSource.REGISTRATION_GOOGLE_ONE_TAP_RP];

                if (validRegistrationFlowSources.includes(authPayload.userSource)) {
                    listenerApi.dispatch(
                        startRegistrationFlow({
                            hasCompleteProfile,
                            hasPhoneVerified,
                            isUserLoggedIn: true,
                            registrationMethod: authPayload.registrationMethod,
                            socialMediaType: authPayload.socialMediaType,
                            userSource: authPayload.userSource,
                            gtmSourceSection: authPayload.gtmSourceSection,
                            modalVariant: "hidden_prices_variant_b"
                        })
                    );
                }
            }
        }
    } catch (error) {
        notifyBugsnag(error, "Failed to update registration flow after authentication", {authPayload});
    }

    if (!userProfile) {
        return;
    }

    try {
        const currentViewType = state.viewType.current;
        (afterRegister ? userSignUpHit : userSignInHit)(authPayload.userSource, userProfile, currentViewType);

        if (afterRegister) {
            const {email, uuid, email_marketing_consent, phone_marketing_consent} = userProfile;

            fetchUserUnsubscribeLink()
                .then((response) => {
                    if (!response) {
                        return;
                    }

                    const {unsubscribe_url_rp, unsubscribe_url_gh} = response;
                    userComUserRegister(uuid, email, email_marketing_consent, phone_marketing_consent, unsubscribe_url_rp, unsubscribe_url_gh);
                })
                .catch((error) => {
                    notifyBugsnag(error, "Failed to fetch unsubscribe link for user.com registration");
                });
        }
    } catch (error) {
        notifyBugsnag(error, "Failed to process after auth tracking", {
            authPayload
        });
    }
};

afterAuthListener.startListening({
    matcher: authSucceeded.match,
    effect: handleAfterAuth
});
import {createListenerMiddleware, ListenerEffect, ThunkDispatch, UnknownAction} from "@reduxjs/toolkit";

import {userComUserRegister} from "@pg-mono/user-com";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {IRootState} from "../../store/store";
import {userSignInHit} from "../../tracking/algolytics_hits/user_sign_in_hit";
import {userSignUpHit} from "../../tracking/algolytics_hits/user_sign_up_hit";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {fetchUserUnsubscribeLink} from "../../user/api/fetch_user_unsubscribe_link";
import {getUserProfileApi} from "../../user/api/get_user_profile";
import {IUserProfile} from "../../user/types/IUserProfile";
import {authSucceeded, IAuthSucceededPayload, IPostAuthFlowContext} from "../actions/auth_succeeded_event";
import {closeFlow, nextStep, RegistrationFlowStep, startRegistrationFlow} from "../state/registration_flow_slice";
import {AuthActionType} from "../types/AuthActionType";

/**
 * Listener for handling post-authentication actions (login or registration)
 * This should be triggered after user logs in or registers via login/register form
 * without use of kontomieszkaniowe.pl
 *
 * Use cases:
 * - Two-step application forms [CU-2t405gt]
 * - Hidden prices [CU-862j5xdx7]
 * - Registration after application sent
 */
export const afterAuthListener = createListenerMiddleware<IRootState>();

const USER_PROFILE_WAIT_TIMEOUT_MS = 20000;
type AfterAuthDispatch = ThunkDispatch<IRootState, unknown, UnknownAction>;

type RegistrationFlowSnapshot = Pick<
    IRootState["registrationFlow"],
    "isActive" | "currentStep" | "propertyId" | "offerId" | "vendorId" | "userSource" | "useLeadAfterRegistration"
>;

const getRegistrationFlowSnapshot = (registrationFlow: IRootState["registrationFlow"]): RegistrationFlowSnapshot => ({
    isActive: registrationFlow.isActive,
    currentStep: registrationFlow.currentStep,
    propertyId: registrationFlow.propertyId,
    offerId: registrationFlow.offerId,
    vendorId: registrationFlow.vendorId,
    userSource: registrationFlow.userSource,
    useLeadAfterRegistration: registrationFlow.useLeadAfterRegistration
});

const getExpectedActiveFlowSnapshot = (fallbackSnapshot: RegistrationFlowSnapshot, postAuthFlowContext?: IPostAuthFlowContext): RegistrationFlowSnapshot => {
    if (!postAuthFlowContext?.flowWasActive) {
        return fallbackSnapshot;
    }

    return {
        isActive: true,
        currentStep: postAuthFlowContext.currentStep ?? fallbackSnapshot.currentStep,
        propertyId: postAuthFlowContext.registrationFlow.propertyId,
        offerId: postAuthFlowContext.registrationFlow.offerId,
        vendorId: postAuthFlowContext.registrationFlow.vendorId,
        userSource: postAuthFlowContext.registrationFlow.userSource,
        useLeadAfterRegistration: postAuthFlowContext.registrationFlow.useLeadAfterRegistration
    };
};

const isSameActiveFlow = (registrationFlow: IRootState["registrationFlow"], snapshot: RegistrationFlowSnapshot) => {
    return (
        registrationFlow.isActive &&
        snapshot.isActive &&
        registrationFlow.currentStep === snapshot.currentStep &&
        registrationFlow.propertyId === snapshot.propertyId &&
        registrationFlow.offerId === snapshot.offerId &&
        registrationFlow.vendorId === snapshot.vendorId &&
        registrationFlow.userSource === snapshot.userSource &&
        registrationFlow.useLeadAfterRegistration === snapshot.useLeadAfterRegistration
    );
};

/**
 * Waits for the profile refresh triggered by `fetchUserDataAfterAuthListener`. Every problem is reported and
 * resolved as `null` instead of aborting the caller - authentication already succeeded, so the post-auth flow
 * has to be resolved either way
 */
const takeUserProfile = async (
    listenerApi: Parameters<ListenerEffect<ReturnType<typeof authSucceeded>, IRootState, AfterAuthDispatch>>[1],
    authPayload: IAuthSucceededPayload
): Promise<IUserProfile | null> => {
    let getUserProfileResult: Awaited<ReturnType<typeof listenerApi.take>>;

    try {
        getUserProfileResult = await listenerApi.take(
            (profileAction) =>
                /**
                 * Other consumers (mounting components, concurrent listeners) emit `getUserProfile` actions that
                 * carry no usable profile and must not end the wait: a deduplicated request is rejected with
                 * `meta.condition`, and the anonymous guard resolves with a null payload while the session cache
                 * is still being revalidated. Skip both and keep waiting for the actual refresh
                 */
                (getUserProfileApi.endpoints.getUserProfile.matchFulfilled(profileAction) && profileAction.payload != null) ||
                (getUserProfileApi.endpoints.getUserProfile.matchRejected(profileAction) && !profileAction.meta.condition),
            USER_PROFILE_WAIT_TIMEOUT_MS
        );
    } catch (error) {
        if (listenerApi.signal?.aborted) {
            return null;
        }

        notifyBugsnag(error, "Failed while waiting for user profile after authentication", {authPayload});
        return null;
    }

    if (!getUserProfileResult) {
        notifyBugsnag(new Error("Timed out while waiting for user profile"), "User profile timeout after authentication", {authPayload});
        return null;
    }

    const profileAction = getUserProfileResult[0];

    if (getUserProfileApi.endpoints.getUserProfile.matchRejected(profileAction)) {
        notifyBugsnag(profileAction.error, "Failed to fetch user profile after authentication", {authPayload});
        return null;
    }

    if (!getUserProfileApi.endpoints.getUserProfile.matchFulfilled(profileAction) || !profileAction.payload) {
        notifyBugsnag(new Error("getUserProfile does not return a valid data"), "Problem with getUserProfile", {authPayload});
        return null;
    }

    return profileAction.payload;
};

export const handleAfterAuth: ListenerEffect<ReturnType<typeof authSucceeded>, IRootState, AfterAuthDispatch> = async (action, listenerApi) => {
    const authPayload = action.payload;
    const flowSnapshotAtAuth = getRegistrationFlowSnapshot(listenerApi.getState().registrationFlow);
    const flowWasActiveAtAuth = Boolean(authPayload.postAuthFlowContext?.flowWasActive || flowSnapshotAtAuth.isActive);
    const expectedActiveFlowSnapshot = getExpectedActiveFlowSnapshot(flowSnapshotAtAuth, authPayload.postAuthFlowContext);

    listenerApi.cancelActiveListeners?.();

    const userProfile = await takeUserProfile(listenerApi, authPayload);

    if (listenerApi.signal?.aborted) {
        return;
    }

    const state = listenerApi.getState();
    const hasCompleteProfile = Boolean(userProfile?.name && userProfile.phone);
    const hasPhoneVerified = Boolean(userProfile?.phone_verified);
    const afterRegister = authPayload.type === AuthActionType.REGISTRATION;

    try {
        if (flowWasActiveAtAuth) {
            if (
                expectedActiveFlowSnapshot.currentStep === RegistrationFlowStep.REGISTRATION &&
                isSameActiveFlow(state.registrationFlow, expectedActiveFlowSnapshot)
            ) {
                if (!userProfile && !afterRegister) {
                    /**
                     * Login succeeded but the profile is unknown, so there is no way to tell which onboarding
                     * step is due. Closing beats guessing - and beats leaving the user on a filled in login
                     * form that never reacts
                     */
                    listenerApi.dispatch(closeFlow());
                } else {
                    listenerApi.dispatch(nextStep({hasPhoneVerified, hasCompleteProfile}));
                }
            }
        } else if (!state.registrationFlow.isActive) {
            const oneTapContext = authPayload.authOrigin === "google_one_tap" ? authPayload.postAuthFlowContext : undefined;

            if (oneTapContext && !oneTapContext.flowWasActive) {
                listenerApi.dispatch(
                    startRegistrationFlow({
                        ...oneTapContext.registrationFlow,
                        isUserLoggedIn: true,
                        hasCompleteProfile,
                        hasPhoneVerified,
                        registrationMethod: authPayload.registrationMethod,
                        socialMediaType: authPayload.socialMediaType ?? "google",
                        userSource: oneTapContext.registrationFlow.userSource ?? authPayload.userSource,
                        gtmSourceSection: oneTapContext.registrationFlow.gtmSourceSection ?? authPayload.gtmSourceSection
                    })
                );
            } else if (afterRegister) {
                const validRegistrationFlowSources: UserSource[] = [UserSource.NAVIGATION, UserSource.REGISTRATION_GOOGLE_ONE_TAP_RP];

                if (validRegistrationFlowSources.includes(authPayload.userSource)) {
                    listenerApi.dispatch(
                        startRegistrationFlow({
                            hasCompleteProfile,
                            hasPhoneVerified,
                            isUserLoggedIn: true,
                            registrationMethod: authPayload.registrationMethod,
                            socialMediaType: authPayload.socialMediaType,
                            userSource: authPayload.userSource,
                            gtmSourceSection: authPayload.gtmSourceSection,
                            modalVariant: "hidden_prices_variant_b"
                        })
                    );
                }
            }
        }
    } catch (error) {
        notifyBugsnag(error, "Failed to update registration flow after authentication", {authPayload});
    }

    if (!userProfile) {
        return;
    }

    try {
        const currentViewType = state.viewType.current;
        (afterRegister ? userSignUpHit : userSignInHit)(authPayload.userSource, userProfile, currentViewType);

        if (afterRegister) {
            const {email, uuid, email_marketing_consent, phone_marketing_consent} = userProfile;

            fetchUserUnsubscribeLink()
                .then((response) => {
                    if (!response) {
                        return;
                    }

                    const {unsubscribe_url_rp, unsubscribe_url_gh} = response;
                    userComUserRegister(uuid, email, email_marketing_consent, phone_marketing_consent, unsubscribe_url_rp, unsubscribe_url_gh);
                })
                .catch((error) => {
                    notifyBugsnag(error, "Failed to fetch unsubscribe link for user.com registration");
                });
        }
    } catch (error) {
        notifyBugsnag(error, "Failed to process after auth tracking", {
            authPayload
        });
    }
};

afterAuthListener.startListening({
    matcher: authSucceeded.match,
    effect: handleAfterAuth
});
